import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { ButtonComponent } from '@webapp/ui';
import { ToastService } from '@webapp/ui/components/toast/toast.service';
import type { CsrfMissingError } from '@webapp/auth/interceptors/xsrf.interceptor';
import { ApiKeyRow } from '../../../interfaces/api-keys.interface';
import { ApiKeysStore } from '../../../store/api-keys-store.service';
import { ApiKeyCreateModalComponent } from '../api-key-create-modal/api-key-create-modal.component';
import { ApiKeyDeleteModalComponent } from '../api-key-delete-modal/api-key-delete-modal.component';
import { ApiKeysListComponent } from '../api-keys-list/api-keys-list.component';

/**
 * Container for the SUPERADMIN API keys tab. The store is component-scoped, so its
 * state is discarded with the tab and every visit fetches fresh data.
 * No console.* here: nothing in the api-keys flow may be logged.
 */
@Component({
	selector: 'app-profile-api-keys',
	standalone: true,
	imports: [ButtonComponent, ApiKeysListComponent, ApiKeyDeleteModalComponent, ApiKeyCreateModalComponent],
	providers: [ApiKeysStore],
	templateUrl: './profile-api-keys.component.html',
	styleUrl: './profile-api-keys.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileApiKeysComponent implements OnInit {
	private readonly store = inject(ApiKeysStore);
	private readonly toastService = inject(ToastService);

	rows = this.store.rows;
	loading = this.store.select('loading');
	loadError = this.store.select('loadError');
	canCreate = this.store.canCreate;

	pendingDelete = signal<ApiKeyRow | null>(null);
	deleting = signal(false);

	/** The create modal is rendered with @if so closing destroys it (and its plaintext key). */
	showCreate = signal(false);

	ngOnInit(): void {
		void this.store.load();
	}

	retry(): void {
		void this.store.load();
	}

	requestCreate(): void {
		if (!this.canCreate()) {
			return;
		}
		this.showCreate.set(true);
	}

	onCreateClosed(event: { created: boolean }): void {
		this.showCreate.set(false);
		if (event.created) {
			void this.store.load();
		}
	}

	onCreateLimitReached(): void {
		this.showCreate.set(false);
		void this.store.load();
	}

	askDelete(row: ApiKeyRow): void {
		this.pendingDelete.set(row);
	}

	cancelDelete(): void {
		// The activar-modal may emit visibleChange twice and the user may click while a
		// request is in flight: both must be harmless.
		if (this.deleting()) {
			return;
		}
		this.pendingDelete.set(null);
	}

	async confirmDelete(): Promise<void> {
		const target = this.pendingDelete();
		if (!target || this.deleting()) {
			return;
		}

		this.deleting.set(true);
		try {
			const result = await this.store.deleteKey(target.id);
			if (result === 'deleted') {
				this.toastService.showToast('Listo', 'Key eliminada', 'success');
			} else {
				this.toastService.showToast('Atención', 'La key ya no existe', 'alert_warning');
			}
		} catch (error) {
			this.handleDeleteError(error);
		} finally {
			this.deleting.set(false);
			this.pendingDelete.set(null);
		}
	}

	private handleDeleteError(error: unknown): void {
		// csrfMissing is a synthetic client-side rejection (no status); 403 and anything else
		// also map to the same generic message, without logout or logging.
		if ((error as Partial<CsrfMissingError>)?.csrfMissing) {
			this.toastService.showToast(
				'Problema de seguridad',
				'No pudimos verificar tu sesión de forma segura. Actualizá la página e intentá de nuevo.',
				'error'
			);
			return;
		}
		this.toastService.showToast('Error', 'No se pudo completar la acción. Intentá nuevamente.', 'error');
	}
}
